import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

const STORAGE_TOKEN = "pinboard_token";
const STORAGE_ACTIVE_EMPLOYEE = "pinboard_active_employee";
const STORAGE_VIEW_MODE = "pinboard_view_mode"; // "desktop" | "mobile"
const STORAGE_THEME = "pinboard_theme"; // "postit" | "serious"

export function AuthProvider({ children }) {
  const [token, setTokenState] = useState(() =>
    localStorage.getItem(STORAGE_TOKEN)
  );
  const [user, setUser] = useState(null); // {id, username, role, ...}
  const [activeEmployee, setActiveEmployeeState] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_ACTIVE_EMPLOYEE);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });
  const [viewMode, setViewModeState] = useState(() => {
    const stored = localStorage.getItem(STORAGE_VIEW_MODE);
    return stored === "mobile" ? "mobile" : "desktop";
  });
  const [theme, setThemeState] = useState(() => {
    const stored = localStorage.getItem(STORAGE_THEME);
    return stored === "serious" ? "serious" : "postit";
  });
  const [checking, setChecking] = useState(!!token);

  const setViewMode = useCallback((mode) => {
    const m = mode === "mobile" ? "mobile" : "desktop";
    localStorage.setItem(STORAGE_VIEW_MODE, m);
    setViewModeState(m);
  }, []);

  const setTheme = useCallback((t) => {
    const v = t === "serious" ? "serious" : "postit";
    localStorage.setItem(STORAGE_THEME, v);
    setThemeState(v);
  }, []);

  // Apply theme class to <html> for global CSS overrides
  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle("theme-serious", theme === "serious");
    root.classList.toggle("theme-postit", theme === "postit");
  }, [theme]);

  // axios auth header (global default)
  useEffect(() => {
    if (token) axios.defaults.headers.common.Authorization = `Bearer ${token}`;
    else delete axios.defaults.headers.common.Authorization;
  }, [token]);

  // Hydrate user info from /me when token present
  useEffect(() => {
    let alive = true;
    const run = async () => {
      if (!token) {
        setUser(null);
        setChecking(false);
        return;
      }
      try {
        const { data } = await axios.get(`${API}/auth/me`);
        if (alive) setUser(data);
      } catch (e) {
        if (alive) {
          // invalid/expired token
          localStorage.removeItem(STORAGE_TOKEN);
          localStorage.removeItem(STORAGE_ACTIVE_EMPLOYEE);
          setTokenState(null);
          setUser(null);
          setActiveEmployeeState(null);
        }
      } finally {
        if (alive) setChecking(false);
      }
    };
    run();
    return () => {
      alive = false;
    };
  }, [token]);

  const login = useCallback(async (username, password) => {
    const { data } = await axios.post(`${API}/auth/login`, {
      username,
      password,
    });
    localStorage.setItem(STORAGE_TOKEN, data.token);
    setTokenState(data.token);
    setUser(data.user);
    setActiveEmployeeState(null);
    localStorage.removeItem(STORAGE_ACTIVE_EMPLOYEE);
    return data.user;
  }, []);

  const verifyPin = useCallback(async (pin) => {
    const { data } = await axios.post(`${API}/auth/verify-pin`, { pin });
    localStorage.setItem(STORAGE_ACTIVE_EMPLOYEE, JSON.stringify(data));
    setActiveEmployeeState(data);
    return data;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(STORAGE_TOKEN);
    localStorage.removeItem(STORAGE_ACTIVE_EMPLOYEE);
    setTokenState(null);
    setUser(null);
    setActiveEmployeeState(null);
  }, []);

  const switchEmployee = useCallback(() => {
    localStorage.removeItem(STORAGE_ACTIVE_EMPLOYEE);
    setActiveEmployeeState(null);
  }, []);

  const value = useMemo(
    () => ({
      token,
      user,
      activeEmployee,
      viewMode,
      setViewMode,
      theme,
      setTheme,
      checking,
      login,
      verifyPin,
      logout,
      switchEmployee,
    }),
    [
      token,
      user,
      activeEmployee,
      viewMode,
      setViewMode,
      theme,
      setTheme,
      checking,
      login,
      verifyPin,
      logout,
      switchEmployee,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
